<template>
  <div>
    <v-dialog v-model="markAsFixedModal" width="66%">
      <v-card>
        <v-card-title>
          {{ $t('fleet_health_ui.mark_as_fixed') }}
        </v-card-title>
        <v-card-text>
          <p>{{ $t('fleet_health_ui.mark_as_fixed_modal_message')}}</p>
          <v-form>
            <v-textarea
              outlined
              :error-messages="markAsFixedErrors"
              :placeholder="$t('fleet_health_ui.mark_as_fixed_reason_placeholder')"
              v-model.trim="markAsFixedReason"/>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn @click="markAsFixedModal = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="primary" @click="markAsFixed" :loading="markingInProgress">
            {{ $t('fleet_health_ui.mark_as_fixed') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card elevation="4">
      <v-card-title class="d-flex justify-space-between">
        <span>{{ $t('fleet_health_ui.unhealthy_vehicles_in_fleet')}}</span>
        <info-modal width="800">
          <v-card-title>
            <h4>{{ $t('fleet_health_ui.unhealthy_vehicles_in_fleet') }}</h4>
          </v-card-title>
          <v-card-text v-html="$t('fleet_health_ui.modal_info.unhealthy_vehicles_in_fleet')"/>
        </info-modal>
      </v-card-title>
      <v-card-text v-if="!loading && items.length > 0">
        <v-data-table
          :items="items"
          :headers="tableFields"
          :page="currentPage"
          :items-per-page="rowsPerPage">
          <template #header v-if="$vuetify.breakpoint.smAndUp">
            <thead>
              <tr>
                <th colspan="1"></th>
                <th colspan="4" class="text-center vf-border-left">
                  {{ $t('sensor_failure') }}
                </th>
                <th colspan="4" class="text-center vf-border-left">
                  {{ $t('temperature_health') }}
                </th>
              </tr>
            </thead>
          </template>
          <template #item.vehicle_id="row">
            <router-link :to="`vehicle/${row.value}`">
              <v-icon color="primary">mdi-truck</v-icon>
              <span class="ml-2">{{ row.item.name }}</span>
            </router-link>
          </template>
          <template #item.customer_name="row">
            {{ row.item.customer_name }}
          </template>
          <template v-for="(field, index) in tableIconFields" v-slot:[`item.${field.value}`]="row">
            <span :key="index">
              <!-- unhealthy for this issue -->
              <span v-if="fieldIsUnhealthy(row.item, field.value)">
                <!-- if there are affected routes, link to them -->
                <v-btn fab text small v-if="getFieldUrl(row.item, field.value)" :to="getFieldUrl(row.item, field.value)">
                  <v-icon color="warning">mdi-alert</v-icon>
                </v-btn>
                <!-- otherwise just show the icon -->
                <v-icon v-else color="warning">mdi-alert</v-icon>
              </span>
              <!-- healthy for this issue -->
              <v-icon v-else color="success">mdi-check-bold</v-icon>
            </span>
          </template>
          <template v-slot:item.cooler-nominal-air-temp-not-reached="row">
            <span>
              <!-- unhealthy for this issue -->
              <span v-if="fieldIsUnhealthy(row.item, 'cooler-nominal-air-temp-not-reached')">
                <!-- if there are affected routes, link to them -->
                <v-btn fab text small v-if="getFieldUrl(row.item, 'cooler-nominal-air-temp-not-reached')" :to="getFieldUrl(row.item, 'cooler-nominal-air-temp-not-reached')">
                  <v-icon color="warning">mdi-alert</v-icon>
                </v-btn>
                <!-- otherwise just show the icon -->
                <v-icon v-else color="warning">mdi-alert</v-icon>
              </span>
              <!-- check if sensor failed -->
              <v-icon v-else-if="fieldIsUnhealthy(row.item, 'no-cooler-sensor-data')" color="warning">mdi-help</v-icon>
              <!-- healthy for this issue -->
              <v-icon v-else color="success">mdi-check-bold</v-icon>
            </span>
          </template>
          <template v-slot:item.freezer-nominal-air-temp-not-reached="row">
            <span>
              <!-- unhealthy for this issue -->
              <span v-if="fieldIsUnhealthy(row.item, 'freezer-nominal-air-temp-not-reached')">
                <!-- if there are affected routes, link to them -->
                <v-btn fab text small v-if="getFieldUrl(row.item, 'freezer-nominal-air-temp-not-reached')" :to="getFieldUrl(row.item, 'freezer-nominal-air-temp-not-reached')">
                  <v-icon color="warning">mdi-alert</v-icon>
                </v-btn>
                <!-- otherwise just show the icon -->
                <v-icon v-else color="warning">mdi-alert</v-icon>
              </span>
              <!-- check if sensor failed -->
              <v-icon v-else-if="fieldIsUnhealthy(row.item, 'no-freezer-sensor-data')" color="warning">mdi-help</v-icon>
              <!-- healthy for this issue -->
              <v-icon v-else color="success">mdi-check-bold</v-icon>
            </span>
          </template>
          <template #days_unhealthy="row">
            {{ row.item.days_unhealthy }} days
          </template>
          <template #item.mark-as-fixed="row">
            <vf-tooltip :text="$t('fleet_health_ui.mark_as_fixed')">
              <v-icon @click="showMarkAsFixedModal(row.item.vehicle_id)">mdi-tools</v-icon>
            </vf-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-text v-else-if="!loading && items.length === 0">
        <h5 class="d-flex justify-center align-center">
          <v-icon color="success" size="50" class="mr-2">mdi-check-bold</v-icon>
          {{$t('fleet_health_ui.no_unhealthy_vehicles')}}
        </h5>
      </v-card-text>
      <v-card-text v-else>
        <v-skeleton-loader type="table"/>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import VehicleIssueRecord from '@/orm/models/VehicleIssueRecord'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import InfoModal from '@/components/shared/InfoModal'
import VfTooltip from '@/components/shared/VfTooltip'
import Moment from 'moment-timezone'
import { and as vuelidateAnd, minLength, required } from 'vuelidate/lib/validators'

import { get, set, each, toArray, flatMap, find, isEmpty } from 'lodash'

export default {
  name: 'UnhealthyVehiclesTable',
  components: {
    InfoModal,
    VfTooltip
  },
  mixins: [
    CurrentUserMixin,
    ValidationMixin
  ],
  props: {
    showAllCustomers: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: true
    },
    fleetHealthData: {
      type: Object,
      default: () => {}
    },
    coolerHealthWarnings: {
      type: Array,
      default: () => []
    },
    freezerHealthWarnings: {
      type: Array,
      default: () => []
    }
  },
  data: function () {
    return {
      markingInProgress: false,
      markAsFixedModal: false,
      vehicleToMarkAsFixed: null,
      markAsFixedReason: '',
      currentPage: 1,
      rowsPerPage: 10,
      sortBy: null,
      sortDesc: false,
      pageOptions: [10, 20, 50, 100]
    }
  },
  validations: {
    markAsFixedReason: {
      requiredMinLength: vuelidateAnd(
        minLength(3),
        required
      )
    }
  },
  computed: {
    // Convenience error message for Vuetify
    markAsFixedErrors () {
      return this.validationError({
        prop: this.$v.markAsFixedReason,
        message: this.$t('required')
      })
    },
    items () {
      const rows = {}
      each(this.fleetHealthData, (data, key) => {
        const customerId = parseInt(key)
        each(data.unhealthy, vehicle => {
          const customer = find(this.customers, x => x.id === customerId)
          const row = {
            vehicle_id: vehicle.vehicle_id,
            name: vehicle.name,
            customer: customer,
            customer_name: customer.name
          }
          each(vehicle.issues, (issue) => {
            if (issue.status === 'unhealthy') {
              set(row, `${issue.type}.unhealthy`, true)
              if (!isEmpty(issue.impacted_route_ids)) {
                const url = `/route/index?route_id=${issue.impacted_route_ids.join(',')}`
                set(row, `${issue.type}.url`, url)
              }
              const issueDate = Moment(issue.time)
              if (!row.oldest_unhealthy_date || issueDate.isBefore(row.oldest_unhealthy_date)) {
                row.oldest_unhealthy_date = issueDate
              }
            }
          })
          if (row.oldest_unhealthy_date) {
            const daysUnhealthy = Moment().diff(row.oldest_unhealthy_date, 'days')
            set(row, 'days_unhealthy', daysUnhealthy)
          }
          rows[vehicle.vehicle_id] = row
        })
      })
      return toArray(rows)
    },

    unhealthyVehicles () {
      return flatMap(this.fleetHealthData, data => {
        return get(data, 'unhealthy', [])
      })
    },

    tableIconFields () {
      return [
        {
          value: RouteViolationRecord.TYPE_SWAPPED_AIR_SENSORS,
          text: this.$tc('swapped_sensor', 2),
          sortable: true,
          align: 'center',
          class: 'vf-border-left',
          cellClass: 'vf-border-left'
        },
        {
          value: VehicleIssueRecord.TYPE_NO_COOLER_SENSOR_DATA,
          text: this.$tc('cooler'),
          sortable: true,
          align: 'center'
        },
        {
          value: VehicleIssueRecord.TYPE_NO_FREEZER_SENSOR_DATA,
          text: this.$tc('freezer'),
          sortable: true,
          align: 'center'
        },
        // For now, we lump both GPS issues (missing and 0,0) together in the table
        {
          value: VehicleIssueRecord.TYPE_NO_GPS_DATA,
          text: this.$tc('gps'),
          sortable: true,
          align: 'center'
        },
        {
          value: 'cooler-' + RouteViolationRecord.TYPE_NOMINAL_AIR_TEMP_NOT_REACHED,
          text: this.$tc('cooler'),
          sortable: true,
          align: 'center',
          class: 'vf-border-left',
          cellClass: 'vf-border-left'
        },
        {
          value: 'freezer-' + RouteViolationRecord.TYPE_NOMINAL_AIR_TEMP_NOT_REACHED,
          text: this.$tc('freezer'),
          sortable: true,
          align: 'center'
        }
      ]
    },

    tableFields () {
      const fields = []
      fields.push({
        value: 'vehicle_id',
        text: this.$tc('vehicle'),
        sortable: true
      })
      each(this.tableIconFields, x => {
        fields.push(x)
      })
      fields.push({
        value: 'days_unhealthy',
        text: this.$tc('days_unhealthy'),
        sortable: true,
        align: 'center',
        class: 'health-text',
        cellClass: 'health-text'
      })
      fields.push({
        value: 'mark-as-fixed',
        text: '',
        sortable: false
      })
      if (this.showAllCustomers) {
        fields.splice(1, 0, {
          value: 'customer_name',
          text: this.$tc('customer'),
          sortable: true
        })
      }
      return fields
    },

    from () {
      return (this.rowsPerPage * (this.currentPage - 1)) + 1
    },

    to () {
      return this.rowsPerPage * this.currentPage
    }
  },
  methods: {

    fieldIsUnhealthy (data, field) {
      return get(data, field + '.unhealthy', false)
    },

    getFieldUrl (data, field) {
      return get(data, field + '.url')
    },

    /**
     * Show the 'mark as fixed' modal
     * @param {Number} vehicleId
     */
    showMarkAsFixedModal (vehicleId) {
      this.vehicleToMarkAsFixed = vehicleId
      this.markAsFixedReason = ''
      this.$v.$reset()
      this.markAsFixedModal = true
    },

    /**
     * Mark a vehicle as fixed. Checks validation and then submits to the API.
     */
    async markAsFixed () {
      // Ensure form is validated
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }
      const vehicleId = this.vehicleToMarkAsFixed
      // If we pass validation then create a note, update the vehicle and emit an event so that dashboard refreshes
      const newNote = {
        resource: 'vehicle',
        entity_id: vehicleId,
        user_id: this.user.id,
        note: this.markAsFixedReason,
        context: 'frontend/form/vehicle/mark-as-fixed'
      }
      try {
        this.markingInProgress = true
        await this.$orm('Note').createOrUpdate(newNote)
        await this.$orm('Vehicle').update(vehicleId, {
          marked_as_fixed: Moment.tz('UTC').format()
        })
        this.markAsFixedModal = false
        this.$emit('vehicleFixed', { vehicle_id: vehicleId })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.markingInProgress = false
      }
    }
  }
}
</script>

<style scoped>
.health-text {
  font-size: 24px !important;
}
</style>
